import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { type ConfirmationResult, type User } from "firebase/auth";
import { auth, RecaptchaVerifier } from "../firebaseConfig";
import { CssVarsProvider } from "@mui/joy/styles";
import GlobalStyles from "@mui/joy/GlobalStyles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import GoogleIcon from "../GoogleIcon";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RightSide from "../components/RightSide";
import Loading from "../components/Loading";
import { ErrorDisplay } from "../components/ErrorDisplay";
import { OTPVerification } from "../components/OTPVerification";
import { PhoneNumberInput } from "../components/PhoneNumberInput";
import { customTheme } from "../config/theme";
import { authService } from "../services/authService";
import { sanitizeUserVisibleMessage } from "../utils/errorMessages";
import { getSafeRedirectUrl } from "../utils/redirectValidator";
import { validators } from "../utils/validators";

type SignInMethod = "initial" | "email" | "phone" | "google";

interface SignInState {
  email: string;
  password: string;
  phone: string;
  loading: boolean;
  error: string | null;
  authLoading: boolean;
  signInMethod: SignInMethod;
  pendingGoogleCredential?: any;
  pendingGoogleEmail?: string;
}

const initialState: SignInState = {
  email: "",
  password: "",
  phone: "",
  loading: false,
  error: null,
  authLoading: true,
  signInMethod: "initial",
};

export default function SignIn() {
  const [state, setState] = useState<SignInState>(initialState);
  const [user, setUser] = useState<User | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const [searchParams] = useSearchParams();
  const redirectUrl = getSafeRedirectUrl(searchParams);

  // Initialize CSRF token on page load
  useEffect(() => {
    console.log("[INIT] Requesting CSRF token");
    fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/csrf-token`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => console.log("[INIT] CSRF token generated:", res.ok))
      .catch((err) => console.error("[INIT] Failed to get CSRF token:", err));
  }, []);

  useEffect(() => {
    document.title = "Sign in | Iskcon Kanpur";
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/user/authcheck`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setState((s) => ({ ...s, authLoading: false }));
      }
    };

    checkAuth();
  }, [redirectUrl]);

  useEffect(() => {
    if (timeRemaining <= 0) return;
    const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeRemaining]);

  useEffect(() => {
    console.log("[EFFECT] signInMethod changed to:", state.signInMethod, "user exists:", !!user);
    setState((s) => ({ ...s, error: null }));
    if (state.signInMethod !== "google" && state.signInMethod !== "phone") {
      console.log("[EFFECT] Clearing user and confirmationResult");
      setUser(null);
      setConfirmationResult(null);
    } else if (state.signInMethod === "google") {
      console.log("[EFFECT] Not clearing for google, user:", !!user);
    } else {
      console.log("[EFFECT] Not clearing for phone, user:", !!user);
    }
  }, [state.signInMethod]);

  const ensureRecaptcha = async () => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      await recaptchaRef.current.render();
    }
    return recaptchaRef.current;
  };

  const handleCompleteLogin = async (currentUser: User) => {
    const completeResult = await authService.completeLogin(currentUser, redirectUrl);
    if (!completeResult.success || !completeResult.redirectUrl) {
      throw new Error(completeResult.error || "Session login failed");
    }
    window.location.href = completeResult.redirectUrl;
  };

  const handleEmailSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    console.log("[SIGNIN] handleEmailSignIn called");
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      console.log("[SIGNIN] Validating email:", state.email);
      const emailValidation = validators.email(state.email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.error);
      }

      console.log("[SIGNIN] Calling signInWithEmail");
      const signinResult = await authService.signInWithEmail(state.email, state.password);
      if (!signinResult.success || !signinResult.user) {
        throw new Error(signinResult.error || "Sign in failed");
      }

      let currentUser = signinResult.user;
      
      // If we have a pending Google credential, link it to preserve all auth methods
      if (state.pendingGoogleCredential && state.pendingGoogleEmail === state.email) {
        console.log("[SIGNIN] User has phone before linking:", currentUser.phoneNumber);
        console.log("[SIGNIN] Linking pending Google credential to user account");
        
        try {
          const linkResult = await authService.linkCredential(currentUser, state.pendingGoogleCredential);
          if (!linkResult.success) {
            console.error("[SIGNIN] Google linking failed:", linkResult.error);
            throw new Error("Failed to link Google account: " + linkResult.error);
          } else {
            console.log("[SIGNIN] Successfully linked Google credential");
            console.log("[SIGNIN] User has phone after linking:", linkResult.user?.phoneNumber);
            currentUser = linkResult.user!;
            
            // Refresh user to ensure we have latest data including phone
            const refreshResult = await authService.refreshUser();
            if (refreshResult.success && refreshResult.user) {
              currentUser = refreshResult.user;
              console.log("[SIGNIN] User refreshed, phone after refresh:", currentUser.phoneNumber);
            }
            
            // Clear pending credential
            setState((s) => ({ ...s, pendingGoogleCredential: undefined, pendingGoogleEmail: undefined }));
          }
        } catch (linkErr: any) {
          console.error("[SIGNIN] Linking error:", linkErr);
          // Don't block signin, but warn user
          setState((s) => ({ ...s, error: "Linked email successfully, but Google account linking failed. Please try linking from settings later." }));
          // Continue with email signin
        }
      }

      console.log("[SIGNIN] User phoneNumber:", currentUser.phoneNumber);
      if (currentUser.phoneNumber) {
        console.log("[SIGNIN] User has phone, completing login");
        await handleCompleteLogin(currentUser);
      } else {
        console.log("[SIGNIN] User has NO phone, asking for phone");
        setUser(currentUser);
        console.log("[SIGNIN] Setting signInMethod to 'email'");
        setState((s) => ({ ...s, signInMethod: "email", loading: false }));
      }
    } catch (err: any) {
      console.error("[SIGNIN] Error:", err);
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Sign in failed"),
        loading: false,
      }));
    }
  };

  const handleGoogleSignIn = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const googleResult = await authService.signInWithGoogle();
      
      // Handle account-exists-with-different-credential error
      if (googleResult.needsLinking && googleResult.email && googleResult.credential) {
        console.log("[GOOGLE] Account exists with different provider:", googleResult.email);
        
        // CRITICAL: Sign out immediately to clean up Firebase state
        // When account-exists error occurs, Firebase may have partially modified the account
        // We need to reset and properly handle linking
        console.log("[GOOGLE] Signing out to clean up Firebase state");
        await authService.signOut();
        
        // Fetch existing sign-in methods for this email
        const methodsResult = await authService.fetchSignInMethodsForEmail(googleResult.email);
        if (!methodsResult.success || methodsResult.methods.length === 0) {
          throw new Error("Could not find existing account. Please sign in with email/password first.");
        }
        
        console.log("[GOOGLE] Existing methods:", methodsResult.methods);
        
        // Store the pending Google credential and email for later linking
        // Switch to email signin so user can authenticate with their existing account
        // Then we'll link the Google credential to preserve all auth methods
        setState((s) => ({
          ...s,
          loading: false,
          email: googleResult.email,
          password: "",
          pendingGoogleCredential: googleResult.credential,
          pendingGoogleEmail: googleResult.email,
          signInMethod: "initial",
          error: `An account already exists with ${googleResult.email}. Please sign in with your email/password to link your Google account.`,
        }));
        return;
      }
      
      if (!googleResult.success || !googleResult.user) {
        throw new Error(googleResult.error || "Google sign in failed");
      }

      console.log("[GOOGLE] Sign in successful:", googleResult.user.email);
      const currentUser = googleResult.user;
      
      if (currentUser.phoneNumber) {
        console.log("[GOOGLE] User has phone, completing login");
        await handleCompleteLogin(currentUser);
      } else {
        console.log("[GOOGLE] User has NO phone, asking for phone");
        setUser(currentUser);
        setState((s) => ({ ...s, signInMethod: "google", loading: false }));
      }
    } catch (err: any) {
      console.error("[GOOGLE] Error:", err);
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Google sign in failed"),
        loading: false,
      }));
    }
  };

  const handlePhoneSignInInitiate = () => {
    console.log("[PHONE] Initiating phone signin");
    setState((s) => ({
      ...s,
      signInMethod: "phone",
      phone: "",
      error: null,
    }));
    setUser(null);
  };

  const handlePhoneOTPSend = async () => {
    console.log("[OTP] handlePhoneOTPSend called, phone:", state.phone);
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      console.log("[OTP] Validating phone");
      const phoneValidation = validators.phone(state.phone);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.error);
      }

      const formattedPhone = "+91" + state.phone.replace(/\D/g, "");
      console.log("[OTP] Formatted phone:", formattedPhone);
      const verifier = await ensureRecaptcha();
      console.log("[OTP] Calling sendPhoneVerificationCode");
      const result = await authService.sendPhoneVerificationCode(formattedPhone, verifier);
      console.log("[OTP] Result:", { success: result.success, hasConfirmationResult: !!result.confirmationResult });
      if (!result.success || !result.confirmationResult) {
        throw new Error(result.error || "Failed to send OTP");
      }

      console.log("[OTP] OTP sent! Setting confirmationResult");
      setConfirmationResult(result.confirmationResult);
      console.log("[OTP] Setting timeRemaining to 120");
      setTimeRemaining(120);
      console.log("[OTP] Setting loading: false");
      setState((s) => ({ ...s, signInMethod: "phone", loading: false }));
      console.log("[OTP] All done");
    } catch (err: any) {
      console.error("[OTP] Error:", err);
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Failed to send OTP"),
        loading: false,
      }));
    }
  };

  const handlePhoneOTPVerify = async (code: string) => {
    console.log("[VERIFY] handlePhoneOTPVerify called, code:", code);
    console.log("[VERIFY] user object exists:", !!user, "email:", user?.email);
    
    // Try to get current user from Firebase Auth as fallback
    const currentAuthUser = auth.currentUser;
    console.log("[VERIFY] Firebase currentUser:", !!currentAuthUser, "email:", currentAuthUser?.email, "phone:", currentAuthUser?.phoneNumber);
    
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      if (!confirmationResult) {
        throw new Error("OTP not sent");
      }

      // Use stored user or fallback to current auth user
      const userToLink = user || currentAuthUser;
      console.log("[VERIFY] userToLink:", !!userToLink, "email:", userToLink?.email);
      
      if (userToLink) {
        // FLOW 1 & 2: User signed in with Email/Google first, now adding phone
        console.log("[VERIFY] Linking phone to existing user:", userToLink.email);
        const credentialResult = authService.createPhoneCredential(confirmationResult, code);
        if (!credentialResult.success || !credentialResult.credential) {
          throw new Error(credentialResult.error || "Failed to create phone credential");
        }

        console.log("[VERIFY] Linking credential to user");
        const linkResult = await authService.linkPhoneCredential(userToLink, credentialResult.credential);
        console.log("[VERIFY] Link result:", { success: linkResult.success, hasUser: !!linkResult.user });
        if (!linkResult.success || !linkResult.user) {
          throw new Error(linkResult.error || "Failed to link phone");
        }

        console.log("[VERIFY] Phone linked successfully! Completing login");
        await handleCompleteLogin(linkResult.user);
      } else {
        // FLOW 3: Direct phone-only signin (no existing email/Google account)
        console.log("[VERIFY] No existing user - direct phone signin");
        console.log("[VERIFY] Verifying OTP to sign in with phone only");
        const verifyResult = await authService.verifyPhoneOTP(confirmationResult, code);
        console.log("[VERIFY] verifyResult:", { success: verifyResult.success, hasUser: !!verifyResult.user });
        if (!verifyResult.success || !verifyResult.user) {
          throw new Error(verifyResult.error || "Verification failed");
        }

        console.log("[VERIFY] Phone signin successful, completing login");
        await handleCompleteLogin(verifyResult.user);
      }
    } catch (err: any) {
      console.error("[VERIFY] Error:", err);
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Verification failed"),
        loading: false,
      }));
    }
  };

  if (state.authLoading) {
    return <Loading />;
  }

  const isPhoneFlow = state.signInMethod === "phone" && confirmationResult;
  console.log("[RENDER] isPhoneFlow calculated:", {
    signInMethod: state.signInMethod,
    hasConfirmationResult: !!confirmationResult,
    isPhoneFlow,
  });

  return (
    <CssVarsProvider theme={customTheme} disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            fontFamily: "Roboto, sans-serif",
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "stretch",
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 4,
            overflowY: "auto",
          }}
        >
          <Header />

          <Box
            sx={{
              maxWidth: "400px",
              mx: "auto",
              width: "100%",
            }}
          >
            {state.signInMethod === "initial" ? (
              <>
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                  Welcome back!
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3 }}>
                  Sign in to your account
                </Typography>

                <form onSubmit={handleEmailSignIn}>
                  <FormControl sx={{ mb: 1 }}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={state.email}
                      onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                      disabled={state.loading}
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 2 }}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={state.password}
                      onChange={(e) => setState((s) => ({ ...s, password: e.target.value }))}
                      disabled={state.loading}
                    />
                  </FormControl>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Checkbox
                      size="sm"
                      label="Remember me"
                      name="persistent"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <Link href="/forget-password" level="body-sm">
                      Forgot your password?
                    </Link>
                  </Box>

                  <ErrorDisplay
                    error={state.error}
                    onDismiss={() => setState((s) => ({ ...s, error: null }))}
                  />

                  <Button fullWidth sx={{ mt: 2 }} loading={state.loading} type="submit">
                    Sign in
                  </Button>
                </form>

                <Divider sx={{ my: 3 }}>Or</Divider>

                <Button
                  fullWidth
                  variant="soft"
                  startDecorator={<GoogleIcon />}
                  onClick={handleGoogleSignIn}
                  loading={state.loading}
                  disabled={state.loading}
                >
                  Sign in with Google
                </Button>

                <Button
                  fullWidth
                  variant="soft"
                  sx={{ mt: 1 }}
                  onClick={handlePhoneSignInInitiate}
                  loading={state.loading}
                  disabled={state.loading}
                >
                  Sign in with Phone
                </Button>

                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Typography level="body-sm">
                    Don&apos;t have an account? <Link href="/signup">Create one</Link>
                  </Typography>
                </Box>
              </>
            ) : state.signInMethod === "phone" ? (
              <>
                {!confirmationResult ? (
                  <>
                    <Typography level="h3" sx={{ mb: 2 }}>
                      Sign in with Phone
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 3 }}>
                      Enter your phone number to receive a verification code
                    </Typography>

                    <ErrorDisplay
                      error={state.error}
                      onDismiss={() => setState((s) => ({ ...s, error: null }))}
                    />

                    <PhoneNumberInput
                      value={state.phone}
                      onChange={(phone) => setState((s) => ({ ...s, phone }))}
                      disabled={state.loading}
                    />

                    <Button
                      fullWidth
                      sx={{ mt: 3 }}
                      loading={state.loading}
                      onClick={handlePhoneOTPSend}
                      disabled={state.phone.replace(/\D/g, "").length !== 10 || state.loading}
                    >
                      Send Code
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography level="h3" sx={{ mb: 2 }}>
                      Verify Phone Number
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 3 }}>
                      Enter the code we sent to your phone
                    </Typography>

                    <ErrorDisplay
                      error={state.error}
                      onDismiss={() => setState((s) => ({ ...s, error: null }))}
                    />

                    {isPhoneFlow && (
                      <OTPVerification
                        phoneNumber={state.phone}
                        onVerify={handlePhoneOTPVerify}
                        onSendCode={handlePhoneOTPSend}
                        loading={state.loading}
                        otpSent={true}
                        timeRemaining={timeRemaining}
                      />
                    )}
                  </>
                )}

                <Button
                  fullWidth
                  variant="plain"
                  sx={{ mt: 3 }}
                  onClick={() =>
                    setState((s) => ({
                      ...s,
                      signInMethod: "initial",
                      phone: "",
                    }))
                  }
                >
                  Back
                </Button>
              </>
            ) : state.signInMethod === "email" ? (
              <>
                <Typography level="h3" sx={{ mb: 2 }}>
                  Email Sign In - Verify Phone
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3 }}>
                  Enter your phone number to complete sign in
                </Typography>

                <ErrorDisplay
                  error={state.error}
                  onDismiss={() => setState((s) => ({ ...s, error: null }))}
                />

                {!isPhoneFlow ? (
                  <>
                    <PhoneNumberInput
                      value={state.phone}
                      onChange={(phone) => setState((s) => ({ ...s, phone }))}
                      disabled={state.loading}
                    />

                    <Button
                      fullWidth
                      sx={{ mt: 3 }}
                      loading={state.loading}
                      onClick={handlePhoneOTPSend}
                      disabled={state.phone.replace(/\D/g, "").length !== 10 || state.loading}
                    >
                      Send Code
                    </Button>
                  </>
                ) : (
                  <OTPVerification
                    phoneNumber={state.phone}
                    onVerify={handlePhoneOTPVerify}
                    onSendCode={handlePhoneOTPSend}
                    loading={state.loading}
                    otpSent={true}
                    timeRemaining={timeRemaining}
                  />
                )}

                <Button
                  fullWidth
                  variant="plain"
                  sx={{ mt: 3 }}
                  onClick={() =>
                    setState((s) => ({
                      ...s,
                      signInMethod: "initial",
                      phone: "",
                    }))
                  }
                >
                  Back
                </Button>
              </>
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 2 }}>
                  Google Sign In - Verify Phone
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3 }}>
                  Enter your phone number to complete sign in
                </Typography>

                <ErrorDisplay
                  error={state.error}
                  onDismiss={() => setState((s) => ({ ...s, error: null }))}
                />

                {!isPhoneFlow ? (
                  <>
                    <PhoneNumberInput
                      value={state.phone}
                      onChange={(phone) => setState((s) => ({ ...s, phone }))}
                      disabled={state.loading}
                    />

                    <Button
                      fullWidth
                      sx={{ mt: 3 }}
                      loading={state.loading}
                      onClick={handlePhoneOTPSend}
                      disabled={state.phone.replace(/\D/g, "").length !== 10 || state.loading}
                    >
                      Send Code
                    </Button>
                  </>
                ) : (
                  <OTPVerification
                    phoneNumber={state.phone}
                    onVerify={handlePhoneOTPVerify}
                    onSendCode={handlePhoneOTPSend}
                    loading={state.loading}
                    otpSent={true}
                    timeRemaining={timeRemaining}
                  />
                )}

                <Button
                  fullWidth
                  variant="plain"
                  sx={{ mt: 3 }}
                  onClick={() =>
                    setState((s) => ({
                      ...s,
                      signInMethod: "initial",
                      phone: "",
                    }))
                  }
                >
                  Back
                </Button>
              </>
            )}
          </Box>

          <Footer />
        </Box>

        <RightSide />
      </Box>

      <div id="recaptcha-container" style={{ display: "none" }} />
    </CssVarsProvider>
  );
}
